import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@mantine/core";
import { useState } from "react";

import { getInterviewAudioPlaybackUrl } from "@/app/api/interview-audio";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

interface VoiceAnswerLinkLazyProps {
  audioKey: string;
  sessionId: string;
  label?: string;
}

interface CachedPlaybackUrl {
  url: string;
  expiresAtMs: number;
}

// Refresh the presigned URL slightly before it actually expires so a click
// near the boundary doesn't open a tab that immediately 403s on S3.
const EXPIRY_SAFETY_MARGIN_MS = 60 * 1000;

/**
 * AS-1830: same recruiter "PLAY VOICE ANSWER" UX as `VoiceAnswerLink`,
 * but for AI Screener 3.0 sessions where the BE has not pre-computed an
 * `audioPlaybackUrl`. The component only knows the persisted `s3://` key
 * from the message content; it exchanges that for a short-lived presigned
 * HTTPS URL on click and opens the recording in a new tab.
 *
 * Resolved-once: the URL is cached with its `expiresAt` so a second click
 * before expiry reuses the same window.open without extra API hits, and a
 * click after expiry transparently re-fetches.
 */
export const VoiceAnswerLinkLazy = ({
  audioKey,
  sessionId,
  label = "PLAY VOICE ANSWER",
}: VoiceAnswerLinkLazyProps) => {
  const track = useTrackAnalytics({ session_id: sessionId });
  const [cachedUrl, setCachedUrl] = useState<CachedPlaybackUrl | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    if (
      cachedUrl &&
      cachedUrl.expiresAtMs - Date.now() > EXPIRY_SAFETY_MARGIN_MS
    ) {
      track("Voice response played");
      window.open(cachedUrl.url, "_blank", "noopener,noreferrer");
      return;
    }

    // Open window synchronously to preserve user-gesture context — async
    // window.open calls are silently blocked by popup blockers in Safari
    // and Firefox on the first click (cache miss).
    const newWindow = window.open("", "_blank");
    setIsLoading(true);
    try {
      const { playbackUrl, expiresAt } = await getInterviewAudioPlaybackUrl(
        sessionId,
        audioKey,
      );
      setCachedUrl({
        url: playbackUrl,
        expiresAtMs: new Date(expiresAt).getTime(),
      });
      track("Voice response played");
      if (newWindow) {
        newWindow.location.href = playbackUrl;
      }
    } catch {
      newWindow?.close();
      showUpwageNotification(
        "Could not load the voice recording. Please try again.",
        NotificationTypes.Error,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      className="max-w-fit px-0"
      loading={isLoading}
      onClick={handleClick}
      rightSection={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
      size="xs"
      variant="transparent"
    >
      {label}
    </Button>
  );
};
